import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const blockedHosts = (import.meta.env.VITE_BLOCKED_SUPABASE_HOSTS as string | undefined)
  ?.split(',')
  .map((s) => s.trim().toLowerCase())
  .filter(Boolean) || [];
const allowBlockedHostInDev = (import.meta.env.VITE_ALLOW_BLOCKED_SUPABASE_IN_DEV as string | undefined) === 'true';

function isBlockedSupabaseHost(): boolean {
  if (!supabaseUrl) return false;
  try {
    const host = new URL(supabaseUrl).host.toLowerCase();
    return blockedHosts.includes(host);
  } catch {
    return false;
  }
}

// Check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  const basicConfigured = !!(supabaseUrl && supabaseAnonKey && 
    supabaseUrl !== 'your_supabase_project_url' && 
    supabaseAnonKey !== 'your_supabase_anon_key' &&
    supabaseUrl !== 'https://default-tenant.local' &&
    supabaseAnonKey !== 'default-tenant-key');

  if (!basicConfigured) return false;
  if (import.meta.env.DEV && isBlockedSupabaseHost() && !allowBlockedHostInDev) return false;
  return true;
};

export function getSupabaseUrl(): string {
  if (!supabaseUrl || !isSupabaseConfigured()) {
    throw new Error(
      'VITE_SUPABASE_URL is missing or invalid. Restart dev server after updating .env.'
    );
  }
  return supabaseUrl;
}

export function getSupabaseAnonKey(): string {
  if (!supabaseAnonKey || !isSupabaseConfigured()) {
    throw new Error(
      'VITE_SUPABASE_ANON_KEY is missing or invalid. Restart dev server after updating .env.'
    );
  }
  return supabaseAnonKey;
}

if (!isSupabaseConfigured()) {
  console.error('Supabase not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file');
  console.error('Current values:', {
    VITE_SUPABASE_URL: supabaseUrl || 'undefined',
    VITE_SUPABASE_ANON_KEY: supabaseAnonKey ? '[REDACTED]' : 'undefined',
    VITE_BLOCKED_SUPABASE_HOSTS: blockedHosts.join(',') || 'undefined',
    VITE_ALLOW_BLOCKED_SUPABASE_IN_DEV: allowBlockedHostInDev ? 'true' : 'false',
  });
}

/**
 * Auto error tickets: a listener (registered by services/autoErrorReporter) is told about every
 * database (/rest/v1/) or edge function (/functions/v1/) response with HTTP status >= 400.
 * The reporter's own bug_reports writes and send-email calls are never passed on (no loops).
 * The response is cloned synchronously, before supabase-js reads the original body.
 */
export type FailedBackendRequestListener = (info: {
  url: string;
  method: string;
  status: number;
  response: Response;
}) => void;

let failedBackendRequestListener: FailedBackendRequestListener | null = null;

export function setFailedBackendRequestListener(listener: FailedBackendRequestListener | null): void {
  failedBackendRequestListener = listener;
}

function isReportableBackendUrl(url: string): boolean {
  if (url.includes('/rest/v1/')) return !url.includes('/rest/v1/bug_reports');
  if (url.includes('/functions/v1/')) return !url.includes('/functions/v1/send-email');
  return false;
}

function notifyFailedBackendRequest(input: RequestInfo | URL, init: RequestInit | undefined, res: Response): void {
  try {
    if (res.status < 400 || !failedBackendRequestListener) return;
    const url = input instanceof Request ? input.url : String(input);
    if (!isReportableBackendUrl(url)) return;
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    failedBackendRequestListener({ url, method, status: res.status, response: res.clone() });
  } catch {
    // Error reporting must never affect the app's own request.
  }
}

/** Retryable fetch for 504/522 and network errors (e.g. Supabase project waking from pause). */
const MAX_RETRIES = 3;
const RETRY_DELAYS_MS = [1000, 2000, 4000];

async function fetchWithRetry(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  let lastError: unknown;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const res = await fetch(input, init);
      const isRetryable =
        res.status === 504 || // Gateway Timeout
        res.status === 522 || // Connection timed out (e.g. Cloudflare)
        res.status === 503;   // Service Unavailable
      if (isRetryable && attempt < MAX_RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[attempt]));
        continue;
      }
      notifyFailedBackendRequest(input, init, res);
      return res;
    } catch (err) {
      lastError = err;
      // Do not retry browser resource exhaustion (ERR_INSUFFICIENT_RESOURCES) — retries multiply load.
      throw err;
    }
  }
  throw lastError;
}

// Create Supabase client with retry fetch for transient timeouts
const clientUrl = isSupabaseConfigured() ? supabaseUrl : 'https://placeholder.supabase.co';
const clientKey = isSupabaseConfigured() ? supabaseAnonKey : 'placeholder-key';

export const supabase = createClient(clientUrl, clientKey, {
  global: {
    fetch: fetchWithRetry,
  },
});

// Override fetch for better error handling when not configured
if (!isSupabaseConfigured()) {
  const originalFetch = supabase.rest.fetch;
  supabase.rest.fetch = async (...args) => {
    throw new Error('Supabase is not configured. Please click "Connect to Supabase" in the top right or set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.');
  };
}

// Database types
interface Database {
  public: {
    Tables: {
      users: {
        Row: {
          id: string;
          email: string;
          first_name: string | null;
          last_name: string | null;
          role: string;
          is_active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          email: string;
          first_name?: string | null;
          last_name?: string | null;
          role?: string;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          email?: string;
          first_name?: string | null;
          last_name?: string | null;
          role?: string;
          is_active?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      super_admins: {
        Row: {
          id: string;
          user_id: string;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          user_id: string;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          user_id?: string;
          created_at?: string;
          updated_at?: string;
        };
      };
      customers: {
        Row: {
          id: string;
          company_name: string;
          industry: string | null;
          general_notes: string | null;
          num_certs_generated: number;
          is_paid: boolean;
          last_payment_date: string | null;
          payment_history: any;
          current_batch_certificates: any;
          created_at: string;
          updated_at: string;
          generated_certificates: any;
        };
        Insert: {
          id?: string;
          company_name: string;
          industry?: string | null;
          general_notes?: string | null;
          num_certs_generated?: number;
          is_paid?: boolean;
          last_payment_date?: string | null;
          payment_history?: any;
          current_batch_certificates?: any;
          created_at?: string;
          updated_at?: string;
          generated_certificates?: any;
        };
        Update: {
          id?: string;
          company_name?: string;
          industry?: string | null;
          general_notes?: string | null;
          num_certs_generated?: number;
          is_paid?: boolean;
          last_payment_date?: string | null;
          payment_history?: any;
          current_batch_certificates?: any;
          created_at?: string;
          updated_at?: string;
          generated_certificates?: any;
        };
      };
      locations: {
        Row: {
          id: string;
          customer_id: string;
          address_line_1: string;
          address_line_2: string | null;
          address_line_3: string | null;
          city: string;
          eircode_postcode: string | null;
          county_state: string | null;
          country: string;
          location_contact_name: string | null;
          location_contact_email: string | null;
          location_contact_phone: string | null;
          location_notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          customer_id: string;
          address_line_1: string;
          address_line_2?: string | null;
          address_line_3?: string | null;
          city: string;
          eircode_postcode?: string | null;
          county_state?: string | null;
          country?: string;
          location_contact_name?: string | null;
          location_contact_email?: string | null;
          location_contact_phone?: string | null;
          location_notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          customer_id?: string;
          address_line_1?: string;
          address_line_2?: string | null;
          address_line_3?: string | null;
          city?: string;
          eircode_postcode?: string | null;
          county_state?: string | null;
          country?: string;
          location_contact_name?: string | null;
          location_contact_email?: string | null;
          location_contact_phone?: string | null;
          location_notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      contacts: {
        Row: {
          id: string;
          customer_id: string;
          first_name: string;
          last_name: string;
          role: string | null;
          email: string;
          phone_number: string | null;
          is_billing_contact: boolean;
          is_general_contact: boolean;
          contact_notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          customer_id: string;
          first_name: string;
          last_name: string;
          role?: string | null;
          email: string;
          phone_number?: string | null;
          is_billing_contact?: boolean;
          is_general_contact?: boolean;
          contact_notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          customer_id?: string;
          first_name?: string;
          last_name?: string;
          role?: string | null;
          email?: string;
          phone_number?: string | null;
          is_billing_contact?: boolean;
          is_general_contact?: boolean;
          contact_notes?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      email_templates: {
        Row: {
          id: string;
          name: string;
          subject: string;
          body: string;
          is_default: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          name: string;
          subject: string;
          body: string;
          is_default?: boolean;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          name?: string;
          subject?: string;
          body?: string;
          is_default?: boolean;
          created_at?: string;
          updated_at?: string;
        };
      };
      certificate_reminders: {
        Row: {
          id: string;
          customer_id: string;
          company_name: string;
          attendee_name: string;
          course_name: string;
          course_date: string;
          course_dates: string[] | null;
          expiry_date: string;
          is_renewed: boolean;
          marked_renewed_date: string | null;
          last_reminder_sent: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          customer_id: string;
          company_name: string;
          attendee_name: string;
          course_name: string;
          course_date: string;
          course_dates?: string[] | null;
          expiry_date: string;
          is_renewed?: boolean;
          marked_renewed_date?: string | null;
          last_reminder_sent?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          customer_id?: string;
          company_name?: string;
          attendee_name?: string;
          course_name?: string;
          course_date?: string;
          course_dates?: string[] | null;
          expiry_date?: string;
          is_renewed?: boolean;
          marked_renewed_date?: string | null;
          last_reminder_sent?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      review_reminders: {
        Row: {
          id: string;
          customer_id: string;
          company_name: string;
          payment_date: string;
          due_reminder_date: string;
          is_sent: boolean;
          is_completed: boolean;
          last_attempt_date: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          customer_id: string;
          company_name: string;
          payment_date: string;
          due_reminder_date: string;
          is_sent?: boolean;
          is_completed?: boolean;
          last_attempt_date?: string | null;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          customer_id?: string;
          company_name?: string;
          payment_date?: string;
          due_reminder_date?: string;
          is_sent?: boolean;
          is_completed?: boolean;
          last_attempt_date?: string | null;
          created_at?: string;
          updated_at?: string;
        };
      };
      tenant_settings: {
        Row: {
          id: string;
          google_review_link: string;
          certificate_counter: number;
          settings: any;
          created_at: string;
          updated_at: string;
        };
        Insert: {
          id?: string;
          google_review_link?: string;
          certificate_counter?: number;
          settings?: any;
          created_at?: string;
          updated_at?: string;
        };
        Update: {
          id?: string;
          google_review_link?: string;
          certificate_counter?: number;
          settings?: any;
          created_at?: string;
          updated_at?: string;
        };
      };
    };
    Views: {
      [_ in never]: never;
    };
    Functions: {
      get_user_role: {
        Args: Record<PropertyKey, never>;
        Returns: string;
      };
      is_super_admin: {
        Args: Record<PropertyKey, never>;
        Returns: boolean;
      };
    };
    Enums: {
      [_ in never]: never;
    };
  };
}

/**
 * Headers for Supabase Edge Functions from the browser.
 * Gateway requires `apikey` (anon) + `Authorization: Bearer <user access_token>` from the same project.
 */
export async function getSupabaseEdgeFunctionHeaders(): Promise<HeadersInit> {
  if (!isSupabaseConfigured()) {
    throw new Error(
      'Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file'
    );
  }

  const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined)?.trim();
  if (!anonKey) {
    throw new Error('VITE_SUPABASE_ANON_KEY is missing');
  }

  // Validates / refreshes user with server (avoids stale JWT from storage-only getSession).
  const { error: getUserError } = await supabase.auth.getUser();
  if (getUserError) {
    console.warn('getSupabaseEdgeFunctionHeaders: getUser', getUserError.message);
  }

  let {
    data: { session },
  } = await supabase.auth.getSession();

  const now = Date.now();
  const expiresAtMs = session?.expires_at ? session.expires_at * 1000 : 0;
  const refreshIfBefore = now + 120_000; // refresh if expiring within 2 min

  if (!session?.access_token || (expiresAtMs > 0 && expiresAtMs < refreshIfBefore)) {
    const { data, error } = await supabase.auth.refreshSession();
    if (data.session) {
      session = data.session;
    } else if (error) {
      console.warn('getSupabaseEdgeFunctionHeaders: refreshSession failed', error.message);
    }
  }

  if (!session?.access_token) {
    throw new Error('No authentication token available. Please sign in again.');
  }

  const finalExpMs = session.expires_at ? session.expires_at * 1000 : 0;
  if (finalExpMs > 0 && finalExpMs < now) {
    throw new Error('Session expired. Please sign in again.');
  }

  const token = session.access_token.trim();

  return {
    Authorization: `Bearer ${token}`,
    apikey: anonKey,
    'Content-Type': 'application/json',
  };
}

/** Supabase FunctionsHttpError `context.body` may be a string, parsed object, or other. */
function functionsInvokeErrorBodyText(body: unknown): string {
  if (body == null) return '';
  if (typeof body === 'string') {
    try {
      const j = JSON.parse(body) as { error?: string; message?: string };
      if (typeof j.error === 'string') return j.error;
      if (typeof j.message === 'string') return j.message;
    } catch {
      return body.trim();
    }
    return body.trim();
  }
  if (typeof body === 'object') {
    const o = body as { error?: unknown; message?: unknown };
    if (typeof o.error === 'string') return o.error;
    if (typeof o.message === 'string') return o.message;
    try {
      return JSON.stringify(body);
    } catch {
      return String(body);
    }
  }
  return String(body);
}

function parseFunctionsInvokeError(error: unknown): string {
  if (error && typeof error === 'object') {
    const e = error as {
      name?: string;
      message?: string;
      context?: { body?: unknown; status?: number };
    };
    const status = e.context?.status;
    const fromBody = functionsInvokeErrorBodyText(e.context?.body);
    if (fromBody && fromBody !== '{}') return fromBody;
    if (status === 401) {
      return (
        'Unauthorized (401) calling Edge Function. Sign out and sign in again, or confirm VITE_SUPABASE_URL ' +
        'and VITE_SUPABASE_ANON_KEY are for the same Supabase project you logged into.'
      );
    }
    if (e.message && e.message !== 'Edge Function returned a non-2xx status code') return e.message;
    if (typeof status === 'number') return `Edge Function request failed (HTTP ${status}).`;
  }
  return String(error);
}

/** Parses JSON `error` / `message` from {@link invokeEmailProcessor} failures for toast/UI. */
export function formatEmailProcessorError(error: unknown): string {
  return parseFunctionsInvokeError(error);
}

function isInvokeUnauthorized(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as { message?: string; context?: { status?: number; body?: unknown } };
  if (e.context?.status === 401) return true;
  const msg = (e.message || '').toLowerCase();
  if (msg.includes('401') || msg.includes('jwt') || msg.includes('unauthorized')) return true;
  const b = functionsInvokeErrorBodyText(e.context?.body).toLowerCase();
  if (b.includes('invalid jwt') || b.includes('jwt')) return true;
  return false;
}

/**
 * Call `superadmin-api` via `supabase.functions.invoke` with explicit `Authorization` + `apikey`
 * from {@link getSupabaseEdgeFunctionHeaders} (refreshed session). Relying on invoke’s default
 * session headers alone can still yield 401 in some hosts; this matches manual fetch behavior.
 * Sub-routes are passed in `x-invoke-path` (handled by the Edge function).
 */
export async function invokeSuperAdmin(
  pathAndQuery: string,
  options: { method?: string; body?: Record<string, unknown> } = {}
): Promise<unknown> {
  const path = pathAndQuery.startsWith('/') ? pathAndQuery : `/${pathAndQuery}`;
  const run = async () => {
    const base = await getSupabaseEdgeFunctionHeaders();
    const merged = new Headers(base);
    merged.set('x-invoke-path', path);
    const headers: Record<string, string> = {};
    merged.forEach((value, key) => {
      headers[key] = value;
    });

    const { data, error } = await supabase.functions.invoke('superadmin-api', {
      method: options.method ?? 'GET',
      body: options.body,
      headers,
    });
    if (error) {
      const err = new Error(parseFunctionsInvokeError(error));
      (err as Error & { invokeError?: unknown }).invokeError = error;
      throw err;
    }
    return data;
  };

  try {
    return await run();
  } catch (first) {
    const raw = (first as Error & { invokeError?: unknown }).invokeError;
    if (raw && isInvokeUnauthorized(raw)) {
      await supabase.auth.refreshSession();
      return await run();
    }
    throw first;
  }
}

/** Gateway accepts the anon key JWT as Bearer (same value as apikey). Used when user access_token is rejected (e.g. preview / mismatched env). */
function getAnonOnlyEdgeFunctionHeaders(): HeadersInit {
  const anonKey = getSupabaseAnonKey();
  return {
    Authorization: `Bearer ${anonKey}`,
    apikey: anonKey,
    'Content-Type': 'application/json',
  };
}

/**
 * Invoke `email-processor` with plain `fetch` and the project **anon** JWT only.
 * User access_token often returns 401 in Bolt preview / some hosts; we used to retry with anon and the
 * first failed POSTs still appeared as red 401s even though send succeeded. Anon JWT is accepted by
 * the gateway and is already public in the app bundle — same effective access as the old fallback.
 */
export async function invokeEmailProcessor(
  body?: Record<string, unknown>
): Promise<{ data: unknown; error: unknown }> {
  const url = `${getSupabaseUrl().replace(/\/$/, '')}/functions/v1/email-processor`;

  const res = await fetch(url, {
    method: 'POST',
    headers: new Headers(getAnonOnlyEdgeFunctionHeaders()),
    body: JSON.stringify(body ?? {}),
  });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    return {
      data: null,
      error: {
        message: 'Edge Function returned a non-2xx status code',
        context: { status: res.status, body: parsed },
      },
    };
  }
  return { data: parsed, error: null };
}